import {FileUpload, FileValidationCallback, Input, FileUploadInput, TextValidationCallback} from "./types";
import {__} from "@wordpress/i18n";
import {createUploadApplicationDocumentFormData, uploadApplicationDocument} from "./Helper";

const createFileUploadInput = (input: Input, file: FileUpload): FileUploadInput => {
    return {
        el: null as HTMLElement|null,
        file: file,
        input: input,
        name: input.getName(),
        types: {
            'cv': __( 'Lebenslauf', 'dierck-group-company' ),
            'cover-letter': __( 'Anschreiben', 'dierck-group-company' ),
            'employment-reference': __( 'Arbeitszeugnis', 'dierck-group-company' ),
            'certificate': __( 'Zertifikat', 'dierck-group-company' ),
            'work-sample': __( 'Arbeitsprobe', 'dierck-group-company' ),
            'other': __( 'Sonstiges', 'dierck-group-company' )
        },
        _createEl(): HTMLElement {
            const div = document.createElement( 'div' );
            div.classList.add( 'input-upload' );

            div.appendChild( this._createFileUuidInput() );
            div.appendChild( this._createFileNameInput() );
            div.appendChild( this._createFileCategorySelect() );
            div.appendChild( this._createTitle() );
            div.appendChild( this._createMeta() );
            div.appendChild( this._createDeleteButton() );

            return div;
        },
        _createDeleteButton(): HTMLButtonElement {
            const button = document.createElement( 'button' );
            button.classList.add( 'input-upload__delete-button', 'button', 'button--small', 'button--circle', 'button--delete' );
            button.addEventListener( 'click', (event: Event) => {
                event.preventDefault();
                this.remove();
            } );

            return button;
        },
        _createFileCategoryOption( value: string, label: string ): HTMLOptionElement {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = label;
            option.selected = this._getCategory() === value;

            return option;
        },
        _createFileCategorySelect(): HTMLSelectElement {
            const select = document.createElement( 'select' );
            select.classList.add( 'input-upload__type' );
            select.name = `${this.name}[${this.file.uuid}][category]`;

            Object.entries( this.types ).forEach(([value, label]) => {
                select.appendChild( this._createFileCategoryOption( value, label ) );
            });

            return select;
        },
        _createFileNameInput(): HTMLInputElement {
            const input = document.createElement( 'input' );
            input.name = `${this.name}[${this.file.uuid}][original_filename]`;
            input.type  = 'hidden';
            input.value = this.file.original_filename;

            return input;
        },
        _createFileUuidInput(): HTMLInputElement {
            const input = document.createElement( 'input' );
            input.name = `${this.name}[${this.file.uuid}][uuid]`;
            input.type = 'hidden';
            input.value = this.file.uuid;

            return input;
        },
        _createMeta(): HTMLElement {
            const fileExtension: string = this.file.extension;
            const fileSize: number = Math.round(this.file.size / 1024 / 1024 * 100) / 100;

            const meta = document.createElement( 'div' );
            meta.classList.add( 'input-upload__meta' );
            meta.innerHTML = `.${fileExtension} | ${fileSize} MB`;

            return meta;
        },
        _createTitle(): HTMLElement {
            const title = document.createElement( 'div' );
            title.classList.add( 'input-upload__title' );
            title.textContent = this.file.original_filename;

            return title;
        },
        _getCategory(): string {
            return this._hasCV()
                ? 'other'
                : 'cv';
        },
        _hasCV(): boolean {
            const selects = document.querySelectorAll( `.input-uploads[data-name="${this.name}"] select` ) as NodeListOf<HTMLSelectElement>;

            return Array.from(selects).some((select: HTMLSelectElement) => select.value === 'cv');
        },
        append(): void {
            const container = document.querySelector( `.input-uploads[data-name=${this.name}]` );
            this.el = this._createEl();
            container?.appendChild( this.el );
        },
        remove(): void {
            this.el?.remove();
        }
    }
}

export const createFileInput = (el: HTMLInputElement, validationCallback: FileValidationCallback): Input => {
    const FileInputObject = {
        el: el as HTMLInputElement,
        errors: [] as Array<string>,
        uploads: [] as Array<FileUploadInput>,
        maxSize: 1000000, // TODO: Update Value.

        _init(): void {
            this._bindClass();
            this._bindEvents();
        },

        _bindClass(): void {
            this.addError      = this.addError.bind(this);
            this.addUpload     = this.addUpload.bind(this);
            this.addUploads    = this.addUploads.bind(this);
            this.hasErrors     = this.hasErrors.bind(this);
            this.hasUploads    = this.hasUploads.bind(this);
            this.onChange      = this.onChange.bind(this);
            this.printErrors   = this.printErrors.bind(this);
            this.printUpload   = this.printUpload.bind(this);
            this.printUploads  = this.printUploads.bind(this);
            this.processInput  = this.processInput.bind(this);
            this.resetErrors   = this.resetErrors.bind(this);
            this.resetInput    = this.resetInput.bind(this);
            this.markAsInvalid = this.markAsInvalid.bind(this);
            this.markAsValid   = this.markAsValid.bind(this);
            this.uploadFile    = this.uploadFile.bind(this);
            this.uploadFiles   = this.uploadFiles.bind(this);
            this.validate      = this.validate.bind(this);
        },

        _bindEvents(): void {
            this.el.addEventListener('change', this.onChange );
        },

        addError( message: string ): void {
            this.errors.push( message );
        },

        addUpload( upload: FileUploadInput ): void {
            this.uploads.push(upload);
        },

        addUploads( uploads: Array<FileUploadInput> ): void {
            uploads.map( (upload: FileUploadInput) => this.addUpload( upload ) );
        },

        getName(): string {

            return this.el.dataset.name || this.el.name || '';
        },

        getValue(): Array<any> {

            return this.uploads;
        },

        hasErrors(): boolean {

            return this.errors.length >0;
        },

        hasUploads(): boolean {

            return this.uploads.length >0;
        },

        async onChange(event: Event): Promise<void> {
            this.processInput();
            this.resetErrors();

            try {
                const uploads = await this.uploadFiles();

                this.printUploads( uploads );

                if ( ! this.validate() ) {
                    this.markAsInvalid();
                }
                else {
                    this.markAsValid();
                }
            }
            catch( error ) {
                console.log(error);
                this.markAsInvalid();
            }

            this.printErrors();
            this.resetInput();
        },

        markAsInvalid(): void {
            this.el.classList.add( 'input--invalid' );
        },

        markAsValid(): void {
            this.el.classList.add( 'input--valid' );
        },

        printErrors(): void {
            const wrapper = this.el.parentElement as HTMLElement;
            const oldErrors = wrapper.querySelectorAll( '.input-error' ) as NodeListOf<HTMLElement>;

            if ( oldErrors ) {
                oldErrors.forEach( (error) => error.remove() );
            }

            const newErrors = this.errors;

            if ( newErrors ) {
                newErrors.forEach( (errorMessage) => {
                    const errorEl = document.createElement( 'div' );
                    errorEl.classList.add( 'input-error' );
                    errorEl.innerHTML = errorMessage;
                    wrapper.appendChild( errorEl );
                } );
            }
        },

        printUpload( upload: FileUpload ): void {
            const name: string|undefined = this.el.dataset.name;

            if ( !name ) {
                return;
            }

            const FileUploadInput = createFileUploadInput( this, upload );

            this.addUpload( FileUploadInput );
            FileUploadInput.append();
        },

        printUploads( uploads: Array<FileUpload> ): void {
            uploads.map( (upload: FileUpload) => this.printUpload( upload ) );
        },

        processInput(): void {
            this.el.classList.add( 'input--processing' );
            this.el.disabled = true;
        },

        reset(): void {
            this.resetInput();
            this.resetUploads();
            this.resetErrors();
        },

        resetErrors(): void {
            this.errors = [];
        },

        resetInput(): void {
            this.el.classList.remove( 'input--invalid' );
            this.el.classList.remove( 'input--valid' );
            this.el.classList.remove( 'input--processing' );
            this.el.disabled = false;
            this.el.value = '';
        },

        resetUploads(): void {
            this.uploads.forEach( (upload: FileUploadInput) => upload.remove() );
            this.uploads = [];
        },

        async uploadFile(file: File): Promise<FileUpload> {
            const body = createUploadApplicationDocumentFormData( file );

            if ( file.size > this.maxSize ) {
                throw new Error(
                    __( 'Datei ist zu groß.', 'dierck-group-company' )
                );
            }

            const response = await uploadApplicationDocument( body );

            if ( !response.ok ) {
                throw new Error(
                    __( 'HTTP Error', 'dierck-group-company' )
                );
            }

            const result = await response.json();

            if ( !result.success || !result.data?.document?.uuid ) {
                throw new Error(
                    __( 'Datei konnte nicht hochgeladen werden.', 'dierck-group-company' )
                );
            }

            return result.data.document;
        },

        async uploadFiles(): Promise<FileUpload[]> {
            const files: FileList|null = this.el.files;

            if ( !files || files?.length === 0 ) {
                throw new Error(
                    __( 'Keine Datei(en) ausgewählt', 'dierck-group-company' )
                )
            }

            try {
                const promises = Array.from(files).map(
                    (file: File) => this.uploadFile( file )
                );

                return await Promise.all( promises );
            }
            catch( error ) {
                throw error;
            }
        },

        validate(): boolean {
            validationCallback( this, this.getValue() );

            return this.hasUploads() && !this.hasErrors();
        }
    }

    FileInputObject._init();
    return FileInputObject;
}

export const createTextInput = (el: HTMLInputElement, validationCallback: TextValidationCallback): Input => {
    const TextInputObject = {
        el: el as HTMLInputElement,
        errors: [] as Array<string>,

        _init(): void {
            this._bindClass();
            this._bindEvents();
        },

        _bindClass(): void {
            this.addError      = this.addError.bind(this);
            this.hasErrors     = this.hasErrors.bind(this);
            this.onChange      = this.onChange.bind(this);
            this.printErrors   = this.printErrors.bind(this);
            this.resetErrors   = this.resetErrors.bind(this);
            this.markAsInvalid = this.markAsInvalid.bind(this);
            this.markAsValid   = this.markAsValid.bind(this);
            this.validate      = this.validate.bind(this);
        },

        _bindEvents(): void {
            this.el.addEventListener('change', this.onChange );
        },

        addError( message: string ): void {
            this.errors.push( message );
        },

        getName(): string {

            return this.el.name || ''
        },

        getValue(): string {

            return this.el.value;
        },

        hasErrors(): boolean {

            return this.errors.length >0;
        },

        onChange(event: Event): void {
            this.resetErrors();

            if ( ! this.validate() ) {
                this.markAsInvalid();
            }
            else {
                this.markAsValid();
            }

            this.printErrors();
        },

        markAsInvalid(): void {
            this.el.classList.add( 'input--invalid' );
        },

        markAsValid(): void {
            this.el.classList.add( 'input--valid' );
        },

        printErrors(): void {
            const wrapper = this.el.parentElement as HTMLElement;
            const oldErrors = wrapper.querySelectorAll( '.input-error' ) as NodeListOf<HTMLElement>;

            if ( oldErrors ) {
                oldErrors.forEach( (error) => error.remove() );
            }

            const newErrors = this.errors;

            if ( newErrors ) {
                newErrors.forEach( (errorMessage) => {
                    const errorEl = document.createElement( 'div' );
                    errorEl.classList.add( 'input-error' );
                    errorEl.innerHTML = errorMessage;
                    wrapper.appendChild( errorEl );
                } );
            }
        },

        reset(): void {
            this.el.value = '';
            this.resetInput();
            this.resetErrors();
        },

        resetErrors(): void {
            this.errors = [];
        },

        resetInput(): void {
            this.el.classList.remove( 'input--invalid' );
            this.el.classList.remove( 'input--valid' );
            this.el.disabled = false;
            this.el.value = '';
        },

        validate(): boolean {
            validationCallback( this, this.getValue() );

            return !this.hasErrors();
        }
    }

    TextInputObject._init();
    return TextInputObject;
}
